// InfoTip — a small Info icon that reveals a plain-language glossary definition
// on HOVER (mouse) AND TAP/CLICK (touch) AND keyboard focus.
//
// Why not native `title=`? A `title` tooltip is invisible on touch devices and
// to keyboard/screen-reader users, and it can't be styled. Board members read
// these pages on phones and tablets, so the definition has to be reachable by
// tap. This is a real focus/click popover: accessible (aria-label + role),
// openable with Enter/Space, dismissable with Escape or an outside click.
//
// Definitions come from the central glossary (lib/glossary.ts) so the wording is
// identical everywhere a term appears.
//
// Usage:
//   <InfoTip term="encumbrance" />
//   <th>Remaining <InfoTip term="encumbrance" /></th>

import { useEffect, useId, useRef, useState } from "react";
import { Info } from "lucide-react";
import { GLOSSARY, type GlossaryTerm } from "../../lib/glossary";

interface InfoTipProps {
  term: GlossaryTerm;
  /** Extra classes on the trigger wrapper (e.g. alignment tweaks). */
  className?: string;
  /** Icon size in px (default 13 — sits inline with small labels). */
  size?: number;
}

export default function InfoTip({ term, className, size = 13 }: InfoTipProps) {
  const entry = GLOSSARY[term];
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  // Close on outside click / tap and on Escape. Only wired while open.
  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: PointerEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!entry) return null;

  const ariaLabel = `What is ${entry.label}? ${entry.definition}`;

  return (
    <span
      ref={wrapRef}
      className={`relative inline-flex items-center align-middle ${className ?? ""}`}
      // Mouse hover opens; leaving closes. Touch/keyboard use the button below.
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-describedby={open ? panelId : undefined}
        onClick={(e) => {
          // Tap/click toggles (the primary touch affordance). Stop propagation
          // so clicking the icon inside a sortable <th> doesn't also sort.
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center rounded-full text-theme-muted hover:text-theme-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-dcvfd-accent/50 transition-colors cursor-help align-middle"
      >
        <Info size={size} aria-hidden="true" />
      </button>

      {open && (
        <span
          id={panelId}
          role="tooltip"
          className="absolute left-1/2 top-full z-50 mt-1.5 w-64 -translate-x-1/2 rounded-lg border border-theme-subtle bg-theme-surface px-3 py-2 text-left shadow-xl"
          // The popover itself shouldn't re-trigger the wrapper's leave logic in
          // a way that flickers; keep it inside the hoverable wrapper.
        >
          <span className="block text-xs font-bold uppercase tracking-wider text-theme-primary">
            {entry.label}
          </span>
          <span className="mt-0.5 block text-xs leading-snug text-theme-secondary normal-case tracking-normal font-normal">
            {entry.definition}
          </span>
        </span>
      )}
    </span>
  );
}
